import React, { useState, useEffect } from 'react';
import { any, bool, func, object, string, shape } from 'prop-types';
import { withRouter, useParams } from 'react-router-dom';
import { compose } from 'redux';
import classNames from 'classnames';
import { Button, ModalPortal } from '../../components';
import { propTypes } from '../../util/types';
import { hash53 } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { NotSubscribedIcon, SubscribedIcon } from '../../icons/SearchAgentIcons.js';
import css from './SearchAgent.css';
import InterruptProcessModal from '../InterruptProcessModal/InterruptProcessModal';
import getValidParams from './getValidParams.js';
import { useRawOrigin } from './useRawOrigin.js';
import { getUserCountry } from '../../util/location.js';
import config from '../../config';
import { useGroupToDeleteUrlParam } from './useGroupToDeleteUrlParam.js';

const NOT_SUBSCRIBED = 'notSubscribedYet';
const ALREADY_SUBSCRIBED = 'alreadySubscribed';

const iconConfig = {
    [NOT_SUBSCRIBED]: {
        icon: () => <NotSubscribedIcon />,
    },
    [ALREADY_SUBSCRIBED]: {
        icon: () => <SubscribedIcon />,
    },
};

const SearchAgentComponent = ({
    currentUser,
    urlQueryParams,
    initialType,
    history,
    inProgress,
    updateError,
    onUpdateUserProfileInfo,
}) => {
    const [modalVisible, setModalVisibility] = useState(false);

    const params = useParams();
    const { searchGroupIdToDelete } = params || {};

    const userDefined = currentUser && currentUser.id && currentUser.id.uuid;
    const protectedData = userDefined ? currentUser.attributes.profile.protectedData : {};
    const { searchGroups } = protectedData;
    const isDisabled = !userDefined; // || isListingTypeRider;
    const validParams = getValidParams(urlQueryParams);
    const searchGroupHash = searchGroupIdToDelete || hash53(JSON.stringify(validParams));
    const searchGroupAlreadyExists = searchGroups && searchGroups[searchGroupHash];
    const newSearchGroupMaybe = !searchGroups || !searchGroups[searchGroupHash];
    const type = initialType || (searchGroupAlreadyExists ? ALREADY_SUBSCRIBED : NOT_SUBSCRIBED);

    const redirectToDefaultAddress = () => {
        const resourceLocatorString = createResourceLocatorString(
            'SearchPage',
            routeConfiguration(),
            {},
            { ...validParams, ...(config.initialSearchAddress[getUserCountry()] || {}) }
        ).replace(/%252C/g, '%2C');

        history.replace(resourceLocatorString);
    };

    const closeModal = () => {
        setModalVisibility(false);

        if (searchGroupIdToDelete) {
            redirectToDefaultAddress();
        }
    };

    const createOrDeleteGroup = () => {
        const searchGroupsUpdated = { ...searchGroups };

        if (searchGroupAlreadyExists) {
            delete searchGroupsUpdated[searchGroupHash];
        } else if (newSearchGroupMaybe) {
            searchGroupsUpdated[searchGroupHash] = {
                searchParams: {
                    ...validParams,
                },
                lastViewed: new Date().toString(),
            };
            /** if user moves map and saves search group,
             * use bounds in url where mapSearch=true param is present - bound & origin
             * if user selects value at location field and saves search group,
             * use location bounds as place origin instead - bound & !origin
             */
            const mapSearch = validParams.bounds && validParams.origin;

            if (!mapSearch) {
                const SG = searchGroupsUpdated[searchGroupHash];
                SG.searchParams.origin = SG.searchParams.bounds;
                delete SG.searchParams.bounds;
            }

            const { price } = searchGroupsUpdated[searchGroupHash].searchParams;

            if (price) {
                searchGroupsUpdated[searchGroupHash].searchParams.price = price
                    .split(',')
                    .map(p => p * 100)
                    .join(',');
            }
        }

        const params = {
            protectedData: { ...protectedData, searchGroups: { ...searchGroupsUpdated } },
        };

        onUpdateUserProfileInfo(params).then(() => {
            closeModal();
        });
    };

    const initiateSearchAgent = () => {
        if (!currentUser) {
            return history.push(
                createResourceLocatorString('SignupPage', routeConfiguration(), {}, {})
            );
        }

        if (inProgress || isDisabled) {
            return;
        }

        setModalVisibility(true);
    };

    useRawOrigin({
        urlQueryParams,
        history,
    });
    useGroupToDeleteUrlParam({
        searchGroupAlreadyExists,
        searchGroupIdToDelete,
        redirectToDefaultAddress,
        setModalVisibility,
    });

    const classes = classNames({
        [css.container]: true,
        [css.inProgress]: inProgress,
        [css.alreadySubscribed]: searchGroupAlreadyExists,
        [css.disabled]: isDisabled,
    });

    const showCreateGroupModal = modalVisible && !searchGroupAlreadyExists;
    const showDeleteGroupModal = modalVisible && searchGroupAlreadyExists;

    return (
        <div id="search-agent-component">
            <Button className={classes} onClick={initiateSearchAgent} type="button">
                {iconConfig[type].icon()}
                <span>Suchauftrag</span>
            </Button>
            {showDeleteGroupModal && (
                <InterruptProcessModal
                    id="SearchAgentComponentModal.searchGroupAlreadyExists"
                    isOpen
                    onClose={closeModal}
                    handleInterruption={createOrDeleteGroup}
                >
                    <InterruptProcessModal.Icon />
                    <InterruptProcessModal.Heading />
                    <InterruptProcessModal.Description>
                        Bist Du sicher, dass Du den aktuellen Suchauftrag löschen möchtest?
                    </InterruptProcessModal.Description>
                    <InterruptProcessModal.Footer>
                        <InterruptProcessModal.Sidenote>
                            Du kannst jederzeit weitere Suchaufträge erstellen.
                        </InterruptProcessModal.Sidenote>
                        <InterruptProcessModal.Button>
                            Suchauftrag löschen
                        </InterruptProcessModal.Button>
                    </InterruptProcessModal.Footer>
                </InterruptProcessModal>
            )}
            {showCreateGroupModal && (
                <ModalPortal
                    id="SearchAgentComponentModal"
                    isOpen
                    onClose={closeModal}
                    containerClassName={css.modalContainer}
                    contentClassName={css.modalContent}
                    containerClassNameJoined
                >
                    <h3 className={css.modalHeader}>Dein Suchauftrag</h3>
                    <p className={css.modalParagraph}>
                        Erhalte neue und relevante Suchresultate von deinen aktuellen
                        Sucheinstellungen sofort per E-Mail.
                    </p>
                    <footer className={css.modalFooter}>
                        <p className={css.modalSmallText}>
                            Du kannst deinen Suchauftrag jederzeit löschen oder weitere Suchaufträge
                            erstellen.
                        </p>
                        <Button
                            type="button"
                            className={css.modalActionButton}
                            onClick={createOrDeleteGroup}
                            inProgress={inProgress}
                            disabled={inProgress}
                        >
                            Suchauftrag erstellen
                        </Button>
                    </footer>
                    <span className={css.updateError}>{updateError}</span>
                </ModalPortal>
            )}
        </div>
    );
};

SearchAgentComponent.propTypes = {
    currentUser: propTypes.currentUser,
    urlQueryParams: object.isRequired,
    initialType: string,
    inProgress: bool,
    updateError: any,
    onUpdateUserProfileInfo: func.isRequired,
    history: shape({
        push: func.isRequired,
    }).isRequired,
};

export default compose(withRouter)(SearchAgentComponent);
